import { MoreVertical, RotateCw, Trash2 } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useDeleteInvite } from "../../hooks/use-delete-invite";
import { useInvites } from "../../hooks/use-invites";
import { useResendInvite } from "../../hooks/use-resend-invite";
import type { PendingInvite } from "../../types";
import { formatDate } from "../../utils/format";
import { RoleBadge } from "../role-badge";

export function InvitesList() {
	const { data: invites, isLoading, isError } = useInvites();
	const resend = useResendInvite();
	const remove = useDeleteInvite();
	const { has } = usePermissions();
	const canResend = has("invites:resend");
	const canDelete = has("invites:delete");

	const [toDelete, setToDelete] = useState<PendingInvite | null>(null);
	const [resentId, setResentId] = useState<string | null>(null);

	async function handleResend(id: string) {
		setResentId(null);
		try {
			await resend.mutateAsync(id);
			setResentId(id);
		} catch {}
	}

	async function confirmDelete() {
		if (!toDelete) return;
		try {
			await remove.mutateAsync(toDelete.id);
			setToDelete(null);
		} catch {}
	}

	if (isLoading) {
		return <InvitesListSkeleton />;
	}

	if (isError) {
		return (
			<p className="invites-list__state is-error">
				Não foi possível carregar os convites. Tente novamente.
			</p>
		);
	}

	if (!invites || invites.length === 0) {
		return <p className="invites-list__state">Nenhum convite pendente.</p>;
	}

	return (
		<>
			<ul className="invites-list">
				{invites.map((invite) => (
					<li key={invite.id} className="invites-list__item">
						<div className="invites-list__info">
							<p className="invites-list__email">{invite.email}</p>
							<p className="invites-list__expiry">
								Expira em {formatDate(invite.expiresAt)}
								{resentId === invite.id && (
									<span className="invites-list__resent">
										{" "}
										· convite reenviado
									</span>
								)}
							</p>
						</div>
						<Badge variant="outline" className="invites-list__scope">
							{invite.worksiteId ? "Este canteiro" : "Organização"}
						</Badge>
						<RoleBadge role={invite.role} />
						{(canResend || canDelete) && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										aria-label="Ações do convite"
									>
										<MoreVertical />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									{canResend && (
										<DropdownMenuItem
											onClick={() => handleResend(invite.id)}
											disabled={resend.isPending}
										>
											<RotateCw /> Reenviar
										</DropdownMenuItem>
									)}
									{canDelete && (
										<DropdownMenuItem
											variant="destructive"
											onClick={() => setToDelete(invite)}
										>
											<Trash2 /> Remover
										</DropdownMenuItem>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</li>
				))}
			</ul>

			<Dialog
				open={toDelete != null}
				onOpenChange={(open) => !open && setToDelete(null)}
			>
				<DialogContent className="invites-list__delete-dialog">
					<DialogHeader>
						<DialogTitle>Remover convite</DialogTitle>
						<DialogDescription>
							O convite de <strong>{toDelete?.email}</strong> será revogado e o
							link deixará de funcionar. Esta ação não pode ser desfeita.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="outline" disabled={remove.isPending}>
								Cancelar
							</Button>
						</DialogClose>
						<Button
							variant="destructive"
							onClick={confirmDelete}
							disabled={remove.isPending}
						>
							Remover
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

export function InvitesListSkeleton() {
	return (
		<ul className="invites-list">
			{[0, 1, 2].map((i) => (
				<li key={i} className="invites-list__item">
					<div className="invites-list__info">
						<Skeleton className="invites-list__skeleton-email" />
						<Skeleton className="invites-list__skeleton-expiry" />
					</div>
					<Skeleton className="invites-list__skeleton-scope" />
					<Skeleton className="invites-list__skeleton-role" />
					<Skeleton className="invites-list__skeleton-action" />
				</li>
			))}
		</ul>
	);
}
